import {
  Component,
  forwardRef,
  inject,
  OnDestroy,
  OnInit,
  Signal,
  effect,
  HostBinding,
  input,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormBuilder,
  FormGroup,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
} from '@angular/forms';

import { Subscription, map } from 'rxjs';
import {
  ButtonGroupComponent,
  ButtonGroupOption,
} from '../button-group/button-group.component';
import { DatetimePickerComponent } from '../datetime-picker/datetime-picker.component';
import { FormFieldComponent } from '../form-field/form-field.component';
import { toSignal } from '@angular/core/rxjs-interop';
import { LayoutService } from '../../../../core/services/layout.service';
import { Appointment } from '../../../../core/models/appointment.model';

@Component({
  selector: 'app-appointment-control',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    ButtonGroupComponent,
    DatetimePickerComponent,
    FormFieldComponent
],
  templateUrl: './appointment-control.component.html',
  styleUrls: ['./appointment-control.component.css'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AppointmentControlComponent),
      multi: true,
    },
  ],
})
export class AppointmentControlComponent
  implements ControlValueAccessor, OnInit, OnDestroy
{
  private readonly fb = inject(FormBuilder);
  private readonly layoutService = inject(LayoutService);
  private subs = new Subscription();

  @HostBinding('class.is-mobile-layout')
  get isMobileLayout() {
    return this.layoutService.isMobile();
  }

  protected form: FormGroup;
  protected isStrict: Signal<boolean>;
  readonly orientation = input<'horizontal' | 'vertical'>('horizontal');

  protected appointmentTypes: ButtonGroupOption[] = [
    { label: 'Strict', value: 'strict' },
    { label: 'FCFS', value: 'fcfs' },
  ];

  onChange: (value: Appointment | null) => void = () => {};
  onTouched: () => void = () => {};

  constructor() {
    this.form = this.fb.group({
      type: ['strict'],
      from: [null],
      to: [null],
    });

    this.isStrict = toSignal(
      this.form
        .get('type')!
        .valueChanges.pipe(map((type) => type === 'strict')),
      { initialValue: this.form.get('type')?.value === 'strict' }
    );

    // The effect remains to manage the UI state (enabled/disabled fields)
    effect(() => {
      const isStrict = this.isStrict();
      const toControl = this.form.get('to');

      Promise.resolve().then(() => {
        if (isStrict) {
          if (toControl?.enabled) {
            toControl?.disable({ emitEvent: false });
          }
        } else {
          if (toControl?.disabled) {
            toControl?.enable({ emitEvent: false });
          }
        }
      });
    });
  }

  ngOnInit(): void {
    this.subs.add(
      this.form.valueChanges.subscribe((value) => {
        this.onChange(value);
      })
    );
  }

  writeValue(value: Appointment | null): void {
    if (value) {
      this.form.patchValue(value, { emitEvent: false });
    } else {
      this.form.reset({ type: 'strict' }, { emitEvent: false });
    }
  }

  registerOnChange(fn: (value: Appointment | null) => void): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }
  setDisabledState?(isDisabled: boolean): void {
    if (isDisabled) {
      this.form.disable();
    } else {
      this.form.enable();
    }
  }
  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }
}
